import React, { useState, useEffect, useMemo } from 'react'

// `urlsOrSelector` may be a static array of URLs OR a function (props) => urls.
// The function form lets the wrapped component pick a URL set at render time
// (e.g. paypalScripts needs to load PayPal SDK in vault vs one-time mode
// depending on whether the page is a recurring subscription or not).
const withAsyncScriptLoader = urlsOrSelector => WrappedComponent => {
  function WithAsyncScriptLoader(props) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const rawUrls = typeof urlsOrSelector === 'function'
      ? urlsOrSelector(props)
      : urlsOrSelector
    // Stabilize the urls reference across renders so useEffect doesn't
    // tear down and re-inject script tags every render. JSON.stringify is
    // the cheapest content-equality check for a small string array.
    const urlsKey = JSON.stringify(rawUrls)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const urls = useMemo(() => rawUrls, [urlsKey])

    useEffect(() => {
      let loadedScripts = 0

      const handleLoad = () => {
        loadedScripts += 1
        if (loadedScripts === urls.length) {
          setLoading(false)
          setError(false)
        }
      }

      const handleError = () => {
        setLoading(false)
        setError(true)
      }

      const scriptElements = urls.map(url => {
        const script = document.createElement('script')
        script.src = url
        script.async = true
        script.addEventListener('load', handleLoad)
        script.addEventListener('error', handleError)
        document.body.appendChild(script)
        return script
      })

      return () => {
        scriptElements.forEach(script => {
          script.removeEventListener('load', handleLoad)
          script.removeEventListener('error', handleError)
          document.body.removeChild(script)
        })
      }
    }, [urls])

    return (
      <WrappedComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        isScriptLoaded={!loading}
        isScriptLoadSucceed={!error}
      />
    )
  }

  return WithAsyncScriptLoader
}

export default withAsyncScriptLoader
